// src/pages/DailyBriefPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../UserContext';
import { fetchDailyBrief, fetchWatchlist, removeFromWatchlist } from '../services/watchlistService';

const DailyBriefPage = () => {
  const { userEmail } = useUser();
  const navigate = useNavigate();
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!userEmail) return;
    setLoading(true);
    fetchDailyBrief(userEmail)
      .then((data) => {
        setBrief(data);
        setError('');
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [userEmail]);

  if (!userEmail) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-gray-500 text-lg">Sign in to see your Daily Brief</p>
          <button onClick={() => navigate('/login')} className="btn-primary mt-4">
            Log In
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-lg mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
          {error}
        </div>
      </div>
    );
  }

  const hasMovers = brief?.movers?.length > 0;

  return (
    <div className="max-w-lg mx-auto px-4 py-6 space-y-6">
      {/* Greeting */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          {brief?.greeting} 👋
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          {formatDate(brief?.date)} • {brief?.watchlistCount || 0} stocks watched
        </p>
      </div>

      {/* Portfolio pulse */}
      {hasMovers && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Watchlist Pulse</h2>
            <span className={`text-lg font-bold ${brief.avgChangePct >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {brief.avgChangePct >= 0 ? '+' : ''}{brief.avgChangePct}%
            </span>
          </div>
          <p className="text-xs text-gray-400 mt-1">Average change across your watchlist</p>
        </div>
      )}

      {/* Movers */}
      {hasMovers ? (
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Your Stocks</h2>
          {brief.movers.map((stock) => (
            <MoverCard key={stock.symbol} stock={stock} />
          ))}
        </div>
      ) : (
        <EmptyWatchlist onNavigate={() => navigate('/metrics')} />
      )}

      {/* Signals */}
      {brief?.signals?.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Signals</h2>
          {brief.signals.map((signal, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-3 shadow-sm flex items-start gap-3">
              <span className="text-xl flex-shrink-0">{signal.icon}</span>
              <div className="min-w-0">
                <p className="text-sm text-gray-800 leading-snug">{signal.message}</p>
                <p className="text-xs text-gray-400 mt-0.5">{signal.symbol}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Quick actions */}
      <div className="pt-4 border-t border-gray-100 flex gap-3">
        <button
          onClick={() => navigate('/metrics')}
          className="flex-1 py-2.5 text-sm font-medium text-brand-600 bg-brand-50 rounded-lg hover:bg-brand-100 transition"
        >
          View Metrics
        </button>
        <button
          onClick={() => navigate('/metric-visualisations')}
          className="flex-1 py-2.5 text-sm font-medium text-gray-600 bg-gray-50 rounded-lg hover:bg-gray-100 transition"
        >
          Charts
        </button>
      </div>
    </div>
  );
};

const MoverCard = ({ stock }) => {
  const hasPrice = stock.price != null;
  const isPositive = (stock.changePct || 0) >= 0;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-3 shadow-sm flex items-center justify-between">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-gray-900 truncate">{stock.companyName}</p>
        <p className="text-xs text-gray-400">{stock.symbol}</p>
      </div>
      <div className="text-right flex-shrink-0 ml-3">
        {hasPrice ? (
          <>
            <p className="text-sm font-bold text-gray-900">${stock.price.toFixed(2)}</p>
            <p className={`text-xs font-semibold ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
              {isPositive ? '+' : ''}{stock.changePct?.toFixed(2)}%
            </p>
          </>
        ) : (
          <p className="text-xs text-gray-400">No data</p>
        )}
      </div>
    </div>
  );
};

const EmptyWatchlist = ({ onNavigate }) => (
  <div className="text-center py-8">
    <p className="text-4xl mb-3">☆</p>
    <h3 className="text-lg font-semibold text-gray-800">Your watchlist is empty</h3>
    <p className="text-sm text-gray-500 mt-1 max-w-xs mx-auto">
      Star companies from the Metrics page to track them here every day.
    </p>
    <button onClick={onNavigate} className="btn-primary mt-4 px-6">
      Browse Companies
    </button>
  </div>
);

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
  } catch {
    return dateStr;
  }
};

export default DailyBriefPage;
